import { J2Validate } from '@e3dc-react/icons'
import { Box, Typography, useTheme } from '@mui/material'
import React, { ElementType } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Theme } from '@mui/material/styles'
import { ButtonType } from '../types'

interface TypeStyleProps {
  textColor: string
  primaryColor: string
  secondaryColor: string
}

interface StyleProps {
  isActive: boolean
  typeStyles: TypeStyleProps
}

const getTypeStyles = (theme: Theme): { [K in ButtonType]: TypeStyleProps } => ({
  inProgress: {
    textColor: theme.palette.grey.grey04,
    primaryColor: theme.palette.grey.grey04,
    secondaryColor: theme.palette.grey.grey06,
  },
  completed: {
    textColor: theme.palette.primary.main,
    primaryColor: theme.palette.primary.main,
    secondaryColor: theme.palette.primary.light,
  },
  invalid: {
    textColor: theme.shape.stepIconComponent?.color.errorDark || '',
    primaryColor: theme.shape.stepIconComponent?.color.errorDark || '',
    secondaryColor: theme.shape.stepIconComponent?.color.errorLight || '',
  },
  incomplete: {
    textColor: theme.palette.warning.main,
    primaryColor: theme.palette.warning.main,
    secondaryColor: theme.shape.stepIconComponent?.color.warningLight || '',
  },
})

const useStyles = makeStyles<StyleProps>()((theme, { isActive, typeStyles: { primaryColor, secondaryColor } }) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    padding: '2px 6px 2px 2px',
    borderRadius: '64px',
    backgroundColor: secondaryColor,
    border: 'solid 1px',
    borderColor: isActive ? primaryColor : secondaryColor,
    cursor: 'pointer',
    '&:hover': {
      borderColor: primaryColor,
    },
  },
  circle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 32,
    width: 32,
    borderRadius: '64px',
    marginRight: theme.spacing(2),
    backgroundColor: primaryColor,
  },
  validateIcon: {
    color: theme.palette.primary.contrastText,
  },
  mobileCircle: {
    height: '12px',
    width: '12px',
    borderRadius: '50%',
    backgroundColor: isActive ? secondaryColor : primaryColor,
    border: 'solid 1px',
    borderColor: primaryColor,
  },
}))

interface Props {
  label: string
  stepNumber?: number
  isActive: boolean
  type: ButtonType
  IconComponent?: ElementType
  isMobile: boolean
}

/**
 * A custom step icon component which is used to display a clickable step in a stepper.
 */
const StepIconComponent: React.VFC<Props> = ({ label, isActive, stepNumber, type, IconComponent, isMobile }) => {
  const theme = useTheme()
  const typeStyles = getTypeStyles(theme)[type]
  const { classes } = useStyles({ isActive, typeStyles })

  const CircleContent = IconComponent ? (
    <IconComponent className={classes.validateIcon} />
  ) : type === 'completed' ? (
    <J2Validate className={classes.validateIcon} />
  ) : (
    <Typography variant="subtitle2" color={theme.palette.primary.contrastText}>
      {stepNumber}
    </Typography>
  )

  if (isMobile) return <Box className={classes.mobileCircle} />
  return (
    <Box component="button" className={classes.container}>
      <Box className={classes.circle}>{CircleContent}</Box>
      <Typography variant="subtitle2" marginRight={theme.spacing(2)} color={typeStyles.textColor}>
        {label}
      </Typography>
    </Box>
  )
}

export default StepIconComponent
